import { useMsal } from "@azure/msal-react";
import { GetStaticProps, GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ReactNode, useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";
import { useCurrentUser } from "../common/api/useCurrentUser";
import { DEFAULT_LOCALE } from "../common/constants";
import { fetcher, useTrackingContext } from "../common/hooks";
import { msalInstance } from "../common/services/auth";
import { useRedirectStore } from "../common/store/useRedirectStore";
import { useLiveAnnouncementsStream } from "../features/announcements/api";
import { GlobalAnnouncement } from "../features/announcements/components";
import { INSTALLATION_ROUTES } from "../features/installation/constants/router.constants";
import { Header as InstallationHeader } from "../features/installation/features/layout/components/Header";
import { Header } from "../features/layout/components/Header";
import { Sidebar } from "../features/layout/components/Sidebar";
import { useSetUpSpacesStore } from "../features/set-up-spaces-flow/store";
import { EmailNotificationModal } from "../features/user-notifications/components/EmailNotificationModal";

// load on demand
const SetUpSpacesFlow = dynamic(
    () =>
        import(
            "../features/set-up-spaces-flow/components/SetUpSpacesFlow"
        ).then((module) => module.SetUpSpacesFlow),
    { ssr: false }
);

interface FrameProps {
    children?: ReactNode;
}

const getRedirectUrl = (
    url: string,
    {
        arg,
    }: {
        arg: {
            routeTo: string;
        };
    }
) => fetcher(`${url}?returnTo=${arg.routeTo}`);

const Frame = (props: FrameProps) => {
    const { children } = props;

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const { externalRedirect, setExternalRedirect } = useRedirectStore(),
        { isOpen, reset } = useSetUpSpacesStore();

    const { user, isError: isCurrentUserError } = useCurrentUser({
            revalidateOnFocus: false,
            revalidateIfStale: false,
            revalidateOnReconnect: false,
        }),
        router = useRouter(),
        { instance } = useMsal();

    useLiveAnnouncementsStream({});
    useTrackingContext({ user, isCurrentUserError });

    const activeAccount = instance.getActiveAccount(),
        currentLocale = router.locale || DEFAULT_LOCALE,
        logoutRequest = {
            postLogoutRedirectUri: `/${currentLocale}/spaces`,
            idTokenHint: activeAccount?.idToken,
            mainWindowRedirectUri: `/${currentLocale}/spaces`,
            extraQueryParameters: {
                ui_locales: currentLocale,
            },
        };

    const onLogoutClick = () => {
        reset();
        msalInstance.logoutRedirect(logoutRequest);
    };

    const { trigger: triggerGetRedirectUrl } = useSWRMutation(
        "/api/support-management/v1/redirect",
        getRedirectUrl,
        {
            onSuccess: (data: { url?: string }) => {
                if (data.url) {
                    location.replace(data.url);
                }
            },
        }
    );

    useEffect(() => {
        // set preferred language
        if (
            user?.languageCode &&
            user.languageCode.toLowerCase() !== router?.locale?.toLowerCase()
        ) {
            const { pathname, asPath, query } = router;
            router.push({ pathname, query }, asPath, {
                locale: user.languageCode,
            });
        }
    }, [user?.languageCode, router]);

    useEffect(() => {
        if (externalRedirect) {
            setExternalRedirect(null);
            triggerGetRedirectUrl({ routeTo: externalRedirect });
        }
    }, [externalRedirect, setExternalRedirect, triggerGetRedirectUrl]);

    const isInstallation = Object.values(INSTALLATION_ROUTES).includes(
        router.pathname
    );

    return isInstallation ? (
        <div className={"flex flex-col h-dvh overflow-hidden bg-surface-500"}>
            <GlobalAnnouncement />
            <div className="flex flex-col flex-1 w-full overflow-auto min-h-0">
                <InstallationHeader />
                <main className={"grow overflow-y-auto px-3.5 py-1.5"}>
                    {children}
                </main>
                <EmailNotificationModal />
                <ConfirmDialog />
                {isOpen && <SetUpSpacesFlow />}
            </div>
        </div>
    ) : (
        <div className="flex flex-col h-dvh">
            <div className="relative flex flex-1 min-h-0 overflow-hidden bg-surface-525">
                <Sidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />
                <div className="flex flex-col flex-1 w-full min-w-0 overflow-hidden">
                    <GlobalAnnouncement />
                    <Header
                        onLogoutClick={onLogoutClick}
                        onMenuClick={() => setIsSidebarOpen(true)}
                    />
                    <main className="grow min-h-0 overflow-auto px-5 py-4.5 [@media(max-height:40rem)]:*:min-h-120">
                        {children}
                    </main>
                    <EmailNotificationModal />
                    <ConfirmDialog />
                    {isOpen && <SetUpSpacesFlow />}
                </div>
            </div>
        </div>
    );
};

export const getStaticProps: GetStaticProps = async (
    ctx: GetStaticPropsContext
) => {
    return {
        props: {
            ...(await serverSideTranslations(ctx.locale as string, [
                "common",
                "setUpSpaces",
                "spaces",
                "language",
                "toast",
            ])),
        },
    };
};

export default Frame;
